
// AttendancePage.js - COMPLETE UPDATED IMPLEMENTATION
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import io from 'socket.io-client';
import { 
  fetchDailyAttendance,
  fetchWeeklyTrend,
  fetchMonthlyReport,
  markAttendance,
  addLiveUpdate,
  updateEmployeeStatus,
  setSocketConnected,
  setDepartmentFilter,
  setStatusFilter,
  setSearchFilter,
  clearLiveUpdates
} from "../../../redux/slices/attendanceDashboardSlice";

// Icons
import { 
  Users, CheckCircle, XCircle, Clock, TrendingUp, TrendingDown,
  Calendar, Filter, Search, Download, Trophy, BarChart3, PieChart,
  Activity, Sun, Home, UserCheck, UserX, CalendarDays, Check, X,
  Bell, Settings, Moon, ChevronRight, AlertCircle, Eye, ExternalLink,
  DownloadCloud, RefreshCw, Briefcase, MoreVertical, Coffee
} from 'lucide-react';

const AttendancePage = () => {
  const dispatch = useDispatch();
  
  // Redux state - NEW SLICE STRUCTURE
  const { 
    daily, 
    weekly, 
    monthly,
    liveUpdates,
    filters,
    socketConnected 
  } = useSelector((state) => state.attendanceDashboard);
  
  // Local state
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [viewType, setViewType] = useState('daily');
  const [showLivePanel, setShowLivePanel] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [socket, setSocket] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [theme, setTheme] = useState('dark');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [weeklyRange, setWeeklyRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [monthlyRange, setMonthlyRange] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });

  // ==================== SOCKET SETUP ====================
  useEffect(() => {
    const newSocket = io(
      process.env.VITE_SOCKET_URL || "http://localhost:5000",
      {
        transports: ["websocket", "polling"],
        withCredentials: true,
      }
    );

    setSocket(newSocket);

    /* =========================
       CONNECTION EVENTS
    ========================= */
    newSocket.on("connect", () => {
      console.log("✅ Socket connected");
      dispatch(setSocketConnected(true));

      dispatch(
        addLiveUpdate({
          type: "connection",
          employeeName: "System",
          message: "Live updates connected",
          status: "CONNECTED",
          timestamp: new Date().toISOString(),
        })
      );
    });

    newSocket.on("disconnect", () => {
      console.log("❌ Socket disconnected");
      dispatch(setSocketConnected(false));

      dispatch(
        addLiveUpdate({
          type: "connection",
          employeeName: "System",
          message: "Live updates disconnected",
          status: "DISCONNECTED",
          timestamp: new Date().toISOString(),
        })
      );
    });

    /* =========================
       ATTENDANCE CHECK-IN
       (PUNCH IN)
    ========================= */
    newSocket.on("attendance-checkin", (data) => {
      console.log("🟢 CHECK-IN SOCKET:", data);

      // 🔔 Live activity panel
      dispatch(
        addLiveUpdate({
          type: "checkin",
          employeeId: data.employeeId,
          employeeName: data.name,
          message: "checked in",
          status: "ACTIVE",
          timestamp: data.checkIn,
        })
      );

      // 🔥 SOURCE OF TRUTH → API REFRESH
      dispatch(fetchDailyAttendance(selectedDate));
    });

    /* =========================
       ATTENDANCE CHECK-OUT
       (PUNCH OUT)
    ========================= */
    newSocket.on("attendance-checkout", (data) => {
      console.log("🔵 CHECK-OUT SOCKET:", data);

      dispatch(
        addLiveUpdate({
          type: "checkout",
          employeeId: data.employeeId,
          employeeName: data.name,
          message: "checked out",
          status: "PRESENT",
          timestamp: data.checkOut,
        })
      );

      // 🔥 SOURCE OF TRUTH → API REFRESH
      dispatch(fetchDailyAttendance(selectedDate));
    });

    /* =========================
       GENERIC ATTENDANCE UPDATE
       (OPTIONAL / FUTURE)
    ========================= */
    newSocket.on("attendance-update", (data) => {
      console.log("🟣 ATTENDANCE UPDATE:", data);

      dispatch(
        addLiveUpdate({
          type: data.type || "update",
          employeeId: data.employeeId,
          employeeName: data.name,
          message: data.message || "attendance updated",
          status: data.status,
          timestamp: new Date().toISOString(),
        })
      );

      dispatch(fetchDailyAttendance(selectedDate));
    });

    /* =========================
       CLEANUP
    ========================= */
    return () => {
      if (newSocket) {
        newSocket.disconnect();
        dispatch(setSocketConnected(false));
      }
    };
  }, [dispatch, selectedDate]);

  // ==================== DATA FETCHING ====================
  useEffect(() => {
    const loadData = async () => {
      try {
        if (viewType === 'daily') {
          await dispatch(fetchDailyAttendance(selectedDate)).unwrap();
        } else if (viewType === 'weekly') {
          await dispatch(fetchWeeklyTrend({
            startDate: weeklyRange.startDate,
            endDate: weeklyRange.endDate
          })).unwrap();
        } else if (viewType === 'monthly') {
          await dispatch(fetchMonthlyReport({
            month: monthlyRange.month,
            year: monthlyRange.year
          })).unwrap();
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        // Add error to live updates
        dispatch(addLiveUpdate({
          type: 'error',
          employeeName: 'System',
          message: `Failed to load ${viewType} data`,
          status: 'ERROR',
          timestamp: new Date().toISOString()
        }));
      }
    };

    loadData();
  }, [viewType, selectedDate, weeklyRange, monthlyRange, dispatch]);

  // ==================== FILTER FUNCTIONS ====================
  const getFilteredEmployees = useMemo(() => {
    if (!daily.employees || daily.employees.length === 0) return [];
    
    let filtered = [...daily.employees];
    
    // Apply department filter
    if (filters.department !== 'All') {
      filtered = filtered.filter(emp => 
        emp.department && emp.department === filters.department
      );
    }
    
    // Apply status filter
    if (filters.status !== 'All') {
      filtered = filtered.filter(emp => {
        const empStatus = emp.status?.toUpperCase();
        
        switch (filters.status) {
          case 'PRESENT':
            return empStatus === 'PRESENT';
          case 'ACTIVE':
            return empStatus === 'ACTIVE';
          case 'ABSENT':
            return empStatus === 'ABSENT';
          case 'LATE':
            return emp.isLate === true;
          case 'HALF_DAY':
            return emp.isHalfDay === true;
          case 'LEAVE':
            return empStatus === 'LEAVE' || emp.type === 'LEAVE';
          default:
            return true;
        }
      });
    }
    
    // Apply search filter
    if (filters.search.trim()) {
      const query = filters.search.toLowerCase();
      filtered = filtered.filter(emp => 
        (emp.name && emp.name.toLowerCase().includes(query)) ||
        (emp.employeeId && emp.employeeId.toLowerCase().includes(query)) ||
        (emp.department && emp.department.toLowerCase().includes(query)) ||
        (emp.email && emp.email.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  }, [daily.employees, filters]);

  const getUniqueDepartments = useMemo(() => {
    if (!daily.employees || daily.employees.length === 0) return ['All'];
    const departments = daily.employees
      .map(emp => emp.department)
      .filter(Boolean)
      .filter((dept, index, arr) => arr.indexOf(dept) === index);
    return ['All', ...departments.sort()];
  }, [daily.employees]);

  const getUniqueStatuses = useMemo(() => {
    return [
      'All',
      'PRESENT',
      'ACTIVE', 
      'ABSENT',
      'LATE',
      'HALF_DAY',
      'LEAVE'
    ];
  }, []);

  // ==================== HELPER FUNCTIONS ====================
  const formatTime = (timeString) => {
    if (!timeString) return '--:--';
    try {
      const time = new Date(timeString);
      return time.toLocaleTimeString('en-IN', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatDuration = (hours) => {
    if (!hours || isNaN(hours) || hours === 0) return '0h';
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return minutes > 0 ? `${wholeHours}h ${minutes}m` : `${wholeHours}h`;
  };

  const getStatusColor = (status, isLate = false, isHalfDay = false) => {
    const statusUpper = status?.toUpperCase();
    
    if (isLate) return '#f59e0b'; // warning
    if (isHalfDay) return '#8b5cf6'; // purple
    if (statusUpper === 'PRESENT' || statusUpper === 'ACTIVE') return '#10b981'; // success
    if (statusUpper === 'ABSENT') return '#ef4444'; // danger
    if (statusUpper === 'LEAVE') return '#3b82f6'; // info
    return '#94a3b8'; // gray
  };

  const getStatusIcon = (status, isLate = false, isHalfDay = false) => {
    const statusUpper = status?.toUpperCase();
    
    if (isLate) return <Clock size={16} />;
    if (isHalfDay) return <Sun size={16} />;
    if (statusUpper === 'PRESENT') return <CheckCircle size={16} />;
    if (statusUpper === 'ACTIVE') return <Activity size={16} />;
    if (statusUpper === 'ABSENT') return <XCircle size={16} />;
    if (statusUpper === 'LEAVE') return <Home size={16} />;
    return <Clock size={16} />;
  };

  const getStatusText = (status, isLate = false, isHalfDay = false) => {
    if (isLate) return 'Late';
    if (isHalfDay) return 'Half Day';
    if (status === 'ACTIVE') return 'Active';
    return status || 'Pending';
  };

  const getUpdateMessage = (update) => {
    switch (update.type) {
      case 'checkin':
        return 'checked in';
      case 'checkout':
        return 'checked out';
      case 'late':
        return 'arrived late';
      case 'early':
        return 'left early';
      case 'manual':
        return 'attendance manually updated';
      default:
        return update.message || 'attendance updated';
    }
  };

  // ==================== HANDLERS ====================
  const handleMarkAttendance = async (employeeId, status, employeeName = 'Employee') => {
    try {
      const result = await dispatch(markAttendance({
        employeeId,
        date: selectedDate,
        status: status.toLowerCase(),
        remarks: `Manually marked by admin as ${status}`,
        markedBy: 'admin'
      })).unwrap();

      // Add to live updates
      dispatch(addLiveUpdate({
        type: 'manual',
        employeeId,
        employeeName,
        message: `marked as ${status}`,
        status: status,
        timestamp: new Date().toISOString()
      }));

      // Show success message
      console.log('Attendance marked successfully:', result);
      
      // Refresh daily data
      dispatch(fetchDailyAttendance(selectedDate));

    } catch (error) {
      console.error('Error marking attendance:', error);
      
      // Add error to live updates
      dispatch(addLiveUpdate({
        type: 'error',
        employeeName: 'System',
        message: `Failed to mark attendance: ${error.message || 'Unknown error'}`,
        status: 'ERROR',
        timestamp: new Date().toISOString()
      }));
    }
  };

  const handleExportCSV = () => {
    setExportLoading(true);
    
    // Create CSV content
    let csvContent = "data:text/csv;charset=utf-8,";
    
    // Add headers
    csvContent += "Name,Employee ID,Department,Status,Check In,Check Out,Work Hours,Overtime Hours\n";
    
    // Add data
    getFilteredEmployees.forEach(emp => {
      csvContent += `"${emp.name || ''}","${emp.employeeId || ''}","${emp.department || ''}","${emp.status || ''}","${emp.checkIn || ''}","${emp.checkOut || ''}","${emp.workHours || 0}","${emp.overtimeHours || 0}"\n`;
    });
    
    // Create download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `attendance_${selectedDate || 'report'}.csv`);
    document.body.appendChild(link);
    
    // Trigger download
    link.click();
    document.body.removeChild(link);
    
    setExportLoading(false);
    
    // Add to live updates
    dispatch(addLiveUpdate({
      type: 'export',
      employeeName: 'Admin',
      message: `Exported ${getFilteredEmployees.length} records`,
      status: 'EXPORT',
      timestamp: new Date().toISOString()
    }));
  };

  const handleRefresh = () => {
    if (viewType === 'daily') {
      dispatch(fetchDailyAttendance(selectedDate));
    } else if (viewType === 'weekly') {
      dispatch(fetchWeeklyTrend(weeklyRange));
    } else if (viewType === 'monthly') {
      dispatch(fetchMonthlyReport(monthlyRange));
    }
    
    // Add to live updates
    dispatch(addLiveUpdate({
      type: 'refresh',
      employeeName: 'System',
      message: `Refreshed ${viewType} data`,
      status: 'REFRESH',
      timestamp: new Date().toISOString()
    }));
  };

  const handleClearLiveUpdates = () => {
    dispatch(clearLiveUpdates());
  };

  // ==================== CHART COMPONENTS ====================
  const renderAttendanceChart = () => {
    if (!daily.summary) return null;
    
    const { summary } = daily;
    const total = summary.totalEmployees || 1;
    
    const data = {
      labels: ['Present', 'Active', 'Absent', 'Late', 'Half Day', 'Leave'],
      datasets: [{
        data: [
          summary.present || 0,
          summary.active || 0,
          summary.absent || 0,
          summary.late || 0,
          summary.halfDay || 0,
          summary.leave || 0
        ],
        backgroundColor: [
          '#10b981', // present - green
          '#3b82f6', // active - blue
          '#ef4444', // absent - red
          '#f59e0b', // late - orange
          '#8b5cf6', // half day - purple
          '#64748b'  // leave - gray
        ]
      }]
    };
    
    return (
      <div className="chart-card">
        <div className="chart-header">
          <h4>Daily Attendance Distribution</h4>
          <div className="chart-legend">
            {data.labels.map((label, index) => {
              const value = data.datasets[0].data[index];
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
              return (
                <div key={label} className="legend-item">
                  <span 
                    className="legend-dot" 
                    style={{backgroundColor: data.datasets[0].backgroundColor[index]}}
                  ></span>
                  {label}: {value} ({percentage}%)
                </div>
              );
            })}
          </div>
        </div>
        <div className="chart-visual">
          {data.labels.map((label, index) => {
            const value = data.datasets[0].data[index];
            const percentage = total > 0 ? (value / total) * 100 : 0;
            
            return (
              <div key={label} className="chart-bar-wrapper">
                <div 
                  className="chart-bar"
                  style={{
                    height: `${percentage}%`,
                    backgroundColor: data.datasets[0].backgroundColor[index]
                  }}
                  title={`${label}: ${value} employees (${percentage.toFixed(1)}%)`}
                >
                  <span className="bar-value">{value}</span>
                </div>
                <div className="bar-label">{label}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // const renderWeeklyTrendChart = () => {
  //   if (!weekly.data || weekly.data.length === 0) {
  //     return (
  //       <div className="chart-card">
  //         <h4>Weekly Trend</h4>
  //         <div className="no-data">
  //           <p>No weekly data available</p>
  //         </div>
  //       </div>
  //     );
  //   }
    
  //   const maxAttendance = Math.max(...weekly.data.map(day => day.present || 0));
  //   const maxValue = maxAttendance > 0 ? maxAttendance : 1;
    
  //   return (
  //     <div className="chart-card">
  //       <div className="chart-header">
  //         <h4>Weekly Attendance Trend</h4>
  //         <div className="date-range">
  //           {formatDate(weeklyRange.startDate)} - {formatDate(weeklyRange.endDate)}
  //         </div>
  //       </div>
  //       <div className="weekly-chart">
  //         {weekly.data.map((day, index) => (
  //           <div key={index} className="week-day">
  //             <div className="day-label">
  //               {new Date(day.date).toLocaleDateString('en-IN', { weekday: 'short' })}
  //             </div>
  //             <div className="day-bars">
  //               <div 
  //                 className="day-bar present" 
  //                 style={{ height: `${((day.present || 0) / maxValue) * 100}%` }}
  //                 title={`Present: ${day.present || 0}`}
  //               />
  //               <div 
  //                 className="day-bar late" 
  //                 style={{ height: `${((day.late || 0) / maxValue) * 50}%` }}
  //                 title={`Late: ${day.late || 0}`}
  //               />
  //               <div 
  //                 className="day-bar absent" 
  //                 style={{ height: `${((day.absent || 0) / maxValue) * 100}%` }}
  //                 title={`Absent: ${day.absent || 0}`}
  //               />
  //             </div>
  //             <div className="day-date">
  //               {new Date(day.date).getDate()}
  //             </div>
  //           </div>
  //         ))}
  //       </div>
  //       <div className="chart-legend">
  //         <div className="legend-item">
  //           <span className="legend-dot present"></span>
  //           Present
  //         </div>
  //         <div className="legend-item">
  //           <span className="legend-dot late"></span>
  //           Late
  //         </div>
  //         <div className="legend-item">
  //           <span className="legend-dot absent"></span>
  //           Absent
  //         </div>
  //       </div>
  //     </div>
  //   );
  // };

const renderWeeklyTrendChart = () => {
  // Check if data exists and is an array
  if (!weekly.data || !Array.isArray(weekly.data)) {
    return (
      <div className="chart-card">
        <h4>Weekly Trend</h4>
        <div className="no-data">
          <p>No weekly data available</p>
          <small>Data format issue: {typeof weekly.data}</small>
        </div>
      </div>
    );
  }

  // Check if array is empty
  if (weekly.data.length === 0) {
    return (
      <div className="chart-card">
        <h4>Weekly Trend</h4>
        <div className="no-data">
          <p>No weekly data available</p>
        </div>
      </div>
    );
  }

  try {
    const maxAttendance = Math.max(...weekly.data.map(day => day.present || 0));
    const maxValue = maxAttendance > 0 ? maxAttendance : 1;

    return (
      <div className="chart-card">
        <div className="chart-header">
          <h4>Weekly Attendance Trend</h4>
          <div className="date-range">
            {formatDate(weeklyRange.startDate)} - {formatDate(weeklyRange.endDate)}
          </div>
        </div>
        <div className="weekly-chart">
          {weekly.data.map((day, index) => (
            <div key={index} className="week-day">
              <div className="day-label">
                {new Date(day.date).toLocaleDateString('en-IN', { weekday: 'short' })}
              </div>
              <div className="day-bars">
                <div
                  className="day-bar present"
                  style={{ height: `${((day.present || 0) / maxValue) * 100}%` }}
                  title={`Present: ${day.present || 0}`}
                />
                <div
                  className="day-bar late"
                  style={{ height: `${((day.late || 0) / maxValue) * 50}%` }}
                  title={`Late: ${day.late || 0}`}
                />
                <div
                  className="day-bar absent"
                  style={{ height: `${((day.absent || 0) / maxValue) * 100}%` }}
                  title={`Absent: ${day.absent || 0}`}
                />
              </div>
              <div className="day-date">
                {new Date(day.date).getDate()}
              </div>
            </div>
          ))}
        </div>
        <div className="chart-legend">
          <div className="legend-item">
            <span className="legend-dot present"></span>
            Present
          </div>
          <div className="legend-item">
            <span className="legend-dot late"></span>
            Late
          </div>
          <div className="legend-item">
            <span className="legend-dot absent"></span>
            Absent
          </div>
        </div>
      </div>
    );
  } catch (error) {
    console.error('Error rendering weekly chart:', error);
    return (
      <div className="chart-card">
        <h4>Weekly Trend</h4>
        <div className="no-data">
          <p>Error loading weekly data</p>
          <small>Check console for details</small>
        </div>
      </div>
    );
  }
};
  
  const renderMonthlyReport = () => {
    if (!monthly.data) {
      return (
        <div className="chart-card">
          <h4>Monthly Report</h4>
          <div className="no-data">
            <p>No monthly data available</p>
          </div>
        </div>
      );
    }
    
    const { summary, dailyStats = [] } = monthly.data;
    
    return (
      <div className="monthly-report">
        <div className="chart-card">
          <h4>Monthly Summary - {monthlyRange.month}/{monthlyRange.year}</h4>
          <div className="monthly-stats">
            <div className="monthly-stat">
              <div className="stat-label">Total Days</div>
              <div className="stat-value">{summary?.totalDays || 0}</div>
            </div>
            <div className="monthly-stat">
              <div className="stat-label">Avg. Present</div>
              <div className="stat-value">{summary?.averagePresent?.toFixed(1) || 0}</div>
            </div>
            <div className="monthly-stat">
              <div className="stat-label">Avg. Late</div>
              <div className="stat-value">{summary?.averageLate?.toFixed(1) || 0}</div>
            </div>
            <div className="monthly-stat">
              <div className="stat-label">Best Day</div>
              <div className="stat-value">{summary?.bestDay?.present || 0}</div>
            </div>
          </div>
          
          {dailyStats.length > 0 && (
            <div className="daily-stats-table">
              <h5>Daily Breakdown</h5>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Present</th>
                      <th>Late</th>
                      <th>Absent</th>
                      <th>Attendance %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyStats.slice(0, 10).map((day, index) => (
                      <tr key={index}>
                        <td>{formatDate(day.date)}</td>
                        <td>{day.present || 0}</td>
                        <td>{day.late || 0}</td>
                        <td>{day.absent || 0}</td>
                        <td>
                          <div className="attendance-percent">
                            <div className="percent-bar">
                              <div 
                                className="percent-fill"
                                style={{ width: `${day.attendanceRate || 0}%` }}
                              />
                            </div>
                            <span>{day.attendanceRate?.toFixed(1) || 0}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTopPerformers = () => {
    // Extract top performers from daily data or use sample
    const performers = daily.employees
      ?.filter(emp => emp.status === 'PRESENT' && emp.workHours > 8)
      .sort((a, b) => (b.workHours || 0) - (a.workHours || 0))
      .slice(0, 5)
      .map((emp, index) => ({
        name: emp.name,
        department: emp.department,
        score: Math.min(100, ((emp.workHours || 0) / 8) * 100),
        avatar: emp.name?.charAt(0) || 'E',
        workHours: emp.workHours
      })) || [];
    
    if (performers.length === 0) {
      return (
        <div className="chart-card">
          <h4>Top Performers</h4>
          <div className="no-data">
            <p>No performance data available</p>
          </div>
        </div>
      );
    }
    
    return (
      <div className="top-performers-card">
        <div className="card-header">
          <h4>
            <Trophy size={20} />
            Top Performers (Today)
          </h4>
          <ChevronRight size={20} />
        </div>
        
        <div className="performers-list">
          {performers.map((performer, index) => (
            <div key={performer.name} className="performer-item">
              <div className="performer-rank">
                <div className={`rank-badge rank-${index + 1}`}>
                  {index + 1}
                </div>
              </div>
              
              <div className="performer-avatar">
                {performer.avatar}
              </div>
              
              <div className="performer-info">
                <div className="performer-name">{performer.name}</div>
                <div className="performer-dept">{performer.department}</div>
              </div>
              
              <div className="performer-score">
                <div className="score-bar">
                  <div 
                    className="score-fill"
                    style={{ width: `${performer.score}%` }}
                  />
                </div>
                <div className="score-details">
                  <span className="score-value">{performer.score.toFixed(1)}%</span>
                  <span className="score-hours">{performer.workHours?.toFixed(1)}h</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ==================== RENDER LOADING ====================
  const isLoading = () => {
    switch (viewType) {
      case 'daily': return daily.loading;
      case 'weekly': return weekly.loading;
      case 'monthly': return monthly.loading;
      default: return false;
    }
  };

  if (isLoading()) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading {viewType} data...</p>
      </div>
    );
  }

  // ==================== MAIN RENDER ====================
  return (
    <div className={`dashboard-container ${theme}`}>
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <div className="logo">
            <CalendarDays className="logo-icon" />
            <span className="logo-text">AttendancePro</span>
          </div>
          
          <div className="mobile-menu-btn" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            <div className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
        
        <div className="header-center">
          <h1 className="page-title">Attendance Dashboard</h1>
          <p className="page-subtitle">Real-time monitoring & analytics</p>
        </div>
        
        <div className="header-right">
          <div className="date-selector">
            <Calendar size={18} />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
          
          <button className="theme-toggle" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          
          <button className="refresh-btn" onClick={handleRefresh} title="Refresh Data">
            <RefreshCw size={20} />
          </button>
          
          <button className="notifications-btn">
            <Bell size={20} />
            {liveUpdates.length > 0 && (
              <span className="notification-count">{Math.min(liveUpdates.length, 9)}</span>
            )}
          </button>
          
          <div className="user-profile">
            <div className="avatar">AD</div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="mobile-nav-overlay" onClick={() => setIsMobileMenuOpen(false)}>
            <div className="mobile-nav" onClick={e => e.stopPropagation()}>
              <div className="nav-items">
                <button 
                  className={`nav-item ${viewType === 'daily' ? 'active' : ''}`} 
                  onClick={() => { setViewType('daily'); setIsMobileMenuOpen(false); }}
                >
                  <Calendar size={20} />
                  <span>Daily View</span>
                </button>
                <button 
                  className={`nav-item ${viewType === 'weekly' ? 'active' : ''}`} 
                  onClick={() => { setViewType('weekly'); setIsMobileMenuOpen(false); }}
                >
                  <BarChart3 size={20} />
                  <span>Weekly Report</span>
                </button>
                <button 
                  className={`nav-item ${viewType === 'monthly' ? 'active' : ''}`} 
                  onClick={() => { setViewType('monthly'); setIsMobileMenuOpen(false); }}
                >
                  <PieChart size={20} />
                  <span>Monthly Analytics</span>
                </button>
                <button 
                  className={`nav-item ${viewType === 'analytics' ? 'active' : ''}`} 
                  onClick={() => { setViewType('analytics'); setIsMobileMenuOpen(false); }}
                >
                  <Trophy size={20} />
                  <span>Top Performers</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Desktop Navigation */}
        <nav className="dashboard-nav">
          <div className="nav-items">
            <button 
              className={`nav-item ${viewType === 'daily' ? 'active' : ''}`} 
              onClick={() => setViewType('daily')}
            >
              <Calendar size={20} />
              <span>Daily View</span>
            </button>
            <button 
              className={`nav-item ${viewType === 'weekly' ? 'active' : ''}`} 
              onClick={() => setViewType('weekly')}
            >
              <BarChart3 size={20} />
              <span>Weekly Report</span>
            </button>
            <button 
              className={`nav-item ${viewType === 'monthly' ? 'active' : ''}`} 
              onClick={() => setViewType('monthly')}
            >
              <PieChart size={20} />
              <span>Monthly Analytics</span>
            </button>
            <button 
              className={`nav-item ${viewType === 'analytics' ? 'active' : ''}`} 
              onClick={() => setViewType('analytics')}
            >
              <Trophy size={20} />
              <span>Top Performers</span>
            </button>
          </div>
        </nav>

        {/* Stats Cards - Only for Daily View */}
        {viewType === 'daily' && daily.summary && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon total">
                <Users size={24} />
              </div>
              <div className="stat-content">
                <h3>{daily.summary.totalEmployees || 0}</h3>
                <p>Total Employees</p>
                <div className="stat-trend">
                  <TrendingUp size={14} />
                  <span>Total workforce</span>
                </div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon present">
                <UserCheck size={24} />
              </div>
              <div className="stat-content">
                <h3>{daily.summary.present || 0}</h3>
                <p>Present Today</p>
                <div className="stat-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ 
                        width: `${((daily.summary.present || 0) / (daily.summary.totalEmployees || 1) * 100)}%`,
                        background: '#10b981'
                      }}
                    />
                  </div>
                  <span className="progress-text">
                    {((daily.summary.present || 0) / (daily.summary.totalEmployees || 1) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon absent">
                <UserX size={24} />
              </div>
              <div className="stat-content">
                <h3>{daily.summary.absent || 0}</h3>
                <p>Absent Today</p>
                <div className="stat-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ 
                        width: `${((daily.summary.absent || 0) / (daily.summary.totalEmployees || 1) * 100)}%`,
                        background: '#ef4444'
                      }}
                    />
                  </div>
                  <span className="progress-text">
                    {((daily.summary.absent || 0) / (daily.summary.totalEmployees || 1) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon late">
                <Clock size={24} />
              </div>
              <div className="stat-content">
                <h3>{daily.summary.late || 0}</h3>
                <p>Late Arrivals</p>
                <div className="stat-trend negative">
                  <TrendingDown size={14} />
                  <span>Punctuality rate</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className="content-area">
          {/* Charts Section */}
          <div className="charts-section">
            {viewType === 'daily' && renderAttendanceChart()}
            {viewType === 'weekly' && renderWeeklyTrendChart()}
            {viewType === 'monthly' && renderMonthlyReport()}
            {viewType === 'analytics' && renderTopPerformers()}
          </div>

          {/* Live Updates Panel */}
          {showLivePanel && (
            <div className="live-panel">
              <div className="panel-header">
                <div className="panel-title">
                  <Activity size={18} />
                  <h4>Live Updates</h4>
                  <div className={`connection-status ${socketConnected ? 'connected' : 'disconnected'}`}>
                    <div className="status-dot"></div>
                    <span>{socketConnected ? 'Live' : 'Offline'}</span>
                  </div>
                </div>
                <div className="panel-actions">
                  <button 
                    className="panel-action-btn" 
                    onClick={handleClearLiveUpdates}
                    title="Clear all updates"
                  >
                    Clear
                  </button>
                  <button 
                    className="panel-toggle" 
                    onClick={() => setShowLivePanel(false)}
                    title="Hide panel"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
              
              <div className="updates-list">
                {liveUpdates.length > 0 ? (
                  liveUpdates.slice(0, 8).map((update, index) => (
                    <div key={index} className={`update-item update-${update.type}`}>
                      <div className="update-icon">
                        {update.type === 'checkin' ? '👤' : 
                         update.type === 'checkout' ? '✅' :
                         update.type === 'manual' ? '✏️' :
                         update.type === 'error' ? '❌' : '📝'}
                      </div>
                      <div className="update-content">
                        <div className="update-message">
                          <strong>{update.employeeName}</strong> {update.message}
                          {update.department && (
                            <span className="update-dept"> ({update.department})</span>
                          )}
                        </div>
                        <div className="update-time">
                          {new Date(update.timestamp).toLocaleTimeString([], { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-updates">
                    <Activity size={32} />
                    <p>Waiting for live updates...</p>
                    <small>Attendance activities will appear here</small>
                  </div>
                )}
              </div>
              
              <div className="panel-footer">
                <span className="updates-count">
                  {liveUpdates.length} {liveUpdates.length === 1 ? 'update' : 'updates'}
                </span>
                {!socketConnected && (
                  <span className="reconnect-text">Reconnecting...</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Attendance Table - Only for Daily View */}
        {viewType === 'daily' && (
          <div className="table-section">
            <div className="section-header">
              <h3>
                Today's Attendance 
                {daily.date && (
                  <span className="date-display"> - {formatDate(daily.date)}</span>
                )}
              </h3>
              <div className="section-actions">
                <div className="search-box">
                  <Search size={18} />
                  <input
                    type="text"
                    placeholder="Search by name, ID, or department..."
                    value={filters.search}
                    onChange={(e) => dispatch(setSearchFilter(e.target.value))}
                  />
                  {filters.search && (
                    <button 
                      className="clear-search"
                      onClick={() => dispatch(setSearchFilter(''))}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                
                <div className="filter-group">
                  <select 
                    className="filter-select"
                    value={filters.department}
                    onChange={(e) => dispatch(setDepartmentFilter(e.target.value))}
                  >
                    {getUniqueDepartments.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                  
                  <select 
                    className="filter-select"
                    value={filters.status}
                    onChange={(e) => dispatch(setStatusFilter(e.target.value))}
                  >
                    {getUniqueStatuses.map(status => (
                      <option key={status} value={status}>
                        {status === 'All' ? 'All Status' : status.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </div>
                
                <button 
                  className="export-btn" 
                  onClick={handleExportCSV} 
                  disabled={exportLoading || getFilteredEmployees.length === 0}
                >
                  <Download size={18} />
                  {exportLoading ? 'Exporting...' : `Export (${getFilteredEmployees.length})`}
                </button>
              </div>
            </div>
            
            {getFilteredEmployees.length > 0 ? (
              <div className="table-container">
                <table className="attendance-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Department</th>
                      <th>Check In</th>
                      <th>Check Out</th>
                      <th>Work Hours</th>
                      <th>Overtime</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredEmployees.map((employee) => (
                      <tr key={employee._id}>
                        <td>
                          <div className="employee-cell">
                            <div className="avatar" style={{ 
                              background: getStatusColor(employee.status, employee.isLate, employee.isHalfDay) 
                            }}>
                              {employee.name?.charAt(0) || 'U'}
                            </div>
                            <div>
                              <div className="employee-name">{employee.name}</div>
                              <div className="employee-id">{employee.employeeId}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="dept-tag">{employee.department}</span>
                        </td>
                        <td>
                          <div className="time-cell">
                            {employee.checkIn ? formatTime(employee.checkIn) : '--:--'}
                            {employee.isLate && (
                              <span className="late-indicator" title="Late Arrival">⚠️</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="time-cell">
                            {employee.checkOut ? formatTime(employee.checkOut) : '--:--'}
                          </div>
                        </td>
                        <td>
                          <div className="hours-cell">
                            {formatDuration(employee.workHours || 0)}
                          </div>
                        </td>
                        <td>
                          <div className="overtime-cell">
                            {employee.overtimeHours > 0 ? (
                              <span className="overtime-tag">+{formatDuration(employee.overtimeHours)}</span>
                            ) : '--'}
                          </div>
                        </td>
                        <td>
                          <div 
                            className={`status-badge ${(employee.status || '').toLowerCase()} ${employee.isLate ? 'late' : ''} ${employee.isHalfDay ? 'half-day' : ''}`}
                            style={{
                              backgroundColor: `${getStatusColor(employee.status, employee.isLate, employee.isHalfDay)}15`,
                              color: getStatusColor(employee.status, employee.isLate, employee.isHalfDay)
                            }}
                          >
                            {getStatusIcon(employee.status, employee.isLate, employee.isHalfDay)}
                            {getStatusText(employee.status, employee.isLate, employee.isHalfDay)}
                          </div>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="action-btn view"
                              onClick={() => setSelectedEmployee(employee)}
                              title="View Details"
                            >
                              <Eye size={16} />
                            </button>
                            <button 
                              className="action-btn present"
                              onClick={() => handleMarkAttendance(employee._id, 'PRESENT', employee.name)}
                              title="Mark as Present"
                              disabled={employee.status === 'PRESENT'}
                            >
                              <Check size={16} />
                            </button>
                            <button 
                              className="action-btn absent"
                              onClick={() => handleMarkAttendance(employee._id, 'ABSENT', employee.name)}
                              title="Mark as Absent"
                              disabled={employee.status === 'ABSENT'}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                <div className="table-footer">
                  <div className="pagination-info">
                    Showing {getFilteredEmployees.length} of {daily.employees?.length || 0} employees
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <Calendar size={48} />
                <h4>No attendance records found</h4>
                <p>
                  {filters.search || filters.department !== 'All' || filters.status !== 'All' 
                    ? 'Try adjusting your filters' 
                    : 'No data available for the selected date'}
                </p>
                {(filters.search || filters.department !== 'All' || filters.status !== 'All') && (
                  <button 
                    className="clear-filters-btn"
                    onClick={() => {
                      dispatch(setSearchFilter(''));
                      dispatch(setDepartmentFilter('All'));
                      dispatch(setStatusFilter('All'));
                    }}
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Error Display */}
        {daily.error && (
          <div className="error-alert">
            <AlertCircle size={20} />
            <span>Error loading daily data: {daily.error}</span>
          </div>
        )}
        
        {weekly.error && viewType === 'weekly' && (
          <div className="error-alert">
            <AlertCircle size={20} />
            <span>Error loading weekly data: {weekly.error}</span>
          </div>
        )}
        
        {monthly.error && viewType === 'monthly' && (
          <div className="error-alert">
            <AlertCircle size={20} />
            <span>Error loading monthly data: {monthly.error}</span>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="dashboard-footer">
        <div className="footer-content">
          <p>© 2024 AttendancePro Dashboard. All rights reserved.</p>
          <div className="footer-links">
            <span className="connection-info">
              <div className={`connection-dot ${socketConnected ? 'connected' : 'disconnected'}`}></div>
              {socketConnected ? 'Live Connected' : 'Connection Lost'}
            </span>
            <span className="last-updated">
              Last updated: {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
            </span>
          </div>
        </div>
      </footer>

      {/* Employee Details Modal */}
      {selectedEmployee && showEmployeeModal && (
        <div className="modal-overlay" onClick={() => setShowEmployeeModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Employee Details</h3>
              <button className="modal-close" onClick={() => setShowEmployeeModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="employee-details">
                <div className="detail-row">
                  <span className="detail-label">Name:</span>
                  <span className="detail-value">{selectedEmployee.name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Employee ID:</span>
                  <span className="detail-value">{selectedEmployee.employeeId}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Department:</span>
                  <span className="detail-value">{selectedEmployee.department}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className="detail-value">
                    <div 
                      className={`status-badge ${(selectedEmployee.status || '').toLowerCase()}`}
                      style={{
                        backgroundColor: `${getStatusColor(selectedEmployee.status, selectedEmployee.isLate, selectedEmployee.isHalfDay)}15`,
                        color: getStatusColor(selectedEmployee.status, selectedEmployee.isLate, selectedEmployee.isHalfDay)
                      }}
                    >
                      {getStatusText(selectedEmployee.status, selectedEmployee.isLate, selectedEmployee.isHalfDay)}
                    </div>
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Check In:</span>
                  <span className="detail-value">
                    {selectedEmployee.checkIn ? formatTime(selectedEmployee.checkIn) : '--:--'}
                    {selectedEmployee.isLate && ' (Late)'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Check Out:</span>
                  <span className="detail-value">
                    {selectedEmployee.checkOut ? formatTime(selectedEmployee.checkOut) : '--:--'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Work Hours:</span>
                  <span className="detail-value">{formatDuration(selectedEmployee.workHours || 0)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Overtime:</span>
                  <span className="detail-value">
                    {selectedEmployee.overtimeHours > 0 
                      ? `+${formatDuration(selectedEmployee.overtimeHours)}` 
                      : 'None'}
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn secondary"
                onClick={() => setShowEmployeeModal(false)}
              >
                Close
              </button>
              <button 
                className="modal-btn primary"
                onClick={() => {
                  setShowEmployeeModal(false);
                  // Add your edit logic here
                }}
              >
                Edit Details
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Styles */}
   <style jsx>{`
  /* CSS Variables for Themes */
  :root {
    /* Light Theme */
    --light-bg-primary: #f8fafc;
    --light-bg-secondary: #ffffff;
    --light-bg-card: #ffffff;
    --light-bg-overlay: rgba(248, 250, 252, 0.9);
    --light-text-primary: #0f172a;
    --light-text-secondary: #475569;
    --light-border: #e2e8f0;
    --light-primary: #2563eb;
    --light-success: #059669;
    --light-warning: #d97706;
    --light-danger: #dc2626;
    --light-info: #7c3aed;
    --light-purple: #7c3aed;
    --light-gray-400: #94a3b8;
    
    /* Dark Theme */
    --dark-bg-primary: #0f172a;
    --dark-bg-secondary: #1e293b;
    --dark-bg-card: #1e293b;
    --dark-bg-overlay: rgba(0, 0, 0, 0.7);
    --dark-text-primary: #f8fafc;
    --dark-text-secondary: #94a3b8;
    --dark-border: #334155;
    --dark-primary: #3b82f6;
    --dark-success: #10b981;
    --dark-warning: #f59e0b;
    --dark-danger: #ef4444;
    --dark-info: #8b5cf6;
    --dark-purple: #8b5cf6;
    --dark-gray-400: #94a3b8;
  }

  /* Default Dark Theme */
  .dashboard-container {
    --bg-primary: var(--dark-bg-primary);
    --bg-secondary: var(--dark-bg-secondary);
    --bg-card: var(--dark-bg-card);
    --bg-overlay: var(--dark-bg-overlay);
    --text-primary: var(--dark-text-primary);
    --text-secondary: var(--dark-text-secondary);
    --border: var(--dark-border);
    --primary: var(--dark-primary);
    --success: var(--dark-success);
    --warning: var(--dark-warning);
    --danger: var(--dark-danger);
    --info: var(--dark-info);
    --purple: var(--dark-purple);
    --gray-400: var(--dark-gray-400);
  }

  .dashboard-container.light {
    --bg-primary: var(--light-bg-primary);
    --bg-secondary: var(--light-bg-secondary);
    --bg-card: var(--light-bg-card);
    --bg-overlay: var(--light-bg-overlay);
    --text-primary: var(--light-text-primary);
    --text-secondary: var(--light-text-secondary);
    --border: var(--light-border);
    --primary: var(--light-primary);
    --success: var(--light-success);
    --warning: var(--light-warning);
    --danger: var(--light-danger);
    --info: var(--light-info);
    --purple: var(--light-purple);
    --gray-400: var(--light-gray-400);
  }

  /* Base Styles */
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  .dashboard-container {
    min-height: 100vh;
    background: var(--bg-primary);
    color: var(--text-primary);
    font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    line-height: 1.5;
  }

  /* Header Styles */
  .dashboard-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 2rem;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border);
    position: static;
    top: 0;
    z-index: 1000;
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .logo {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
    background: linear-gradient(135deg, var(--primary), var(--info));
    border-radius: 12px;
    color: white;
  }

  .logo-icon {
    width: 24px;
    height: 24px;
  }

  .logo-text {
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.025em;
  }

  .mobile-menu-btn {
    display: none;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
  }

  .hamburger {
    width: 24px;
    height: 18px;
    position: relative;
  }

  .hamburger span {
    position: absolute;
    width: 100%;
    height: 2px;
    background: var(--text-primary);
    border-radius: 2px;
    transition: all 0.3s ease;
  }

  .hamburger span:nth-child(1) { top: 0; }
  .hamburger span:nth-child(2) { top: 8px; }
  .hamburger span:nth-child(3) { top: 16px; }

  .hamburger.active span:nth-child(1) {
    transform: rotate(45deg);
    top: 8px;
  }

  .hamburger.active span:nth-child(2) {
    opacity: 0;
  }

  .hamburger.active span:nth-child(3) {
    transform: rotate(-45deg);
    top: 8px;
  }

  .header-center {
    text-align: center;
  }

  .page-title {
    font-size: 1.5rem;
    font-weight: 700;
    background: linear-gradient(135deg, var(--primary), var(--info));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 0.25rem;
  }

  .page-subtitle {
    font-size: 0.875rem;
    color: var(--text-secondary);
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .date-selector {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: 8px;
  }

  .date-selector input {
    background: transparent;
    border: none;
    color: var(--text-primary);
    font-size: 0.875rem;
    outline: none;
  }

  .date-selector input::-webkit-calendar-picker-indicator {
    filter: invert(var(--theme-invert, 0.8));
    cursor: pointer;
  }

  .theme-toggle, .refresh-btn, .notifications-btn {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .theme-toggle:hover, .refresh-btn:hover, .notifications-btn:hover {
    background: var(--primary);
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(var(--primary-rgb), 0.3);
  }

  .refresh-btn:hover {
    transform: rotate(180deg) translateY(-1px);
  }

  .notifications-btn {
    position: relative;
  }

  .notification-count {
    position: absolute;
    top: -4px;
    right: -4px;
    background: var(--danger);
    color: white;
    font-size: 0.75rem;
    font-weight: 600;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .user-profile {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .avatar {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, var(--primary), var(--info));
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    color: white;
    font-size: 0.875rem;
  }

  /* Navigation */
  .dashboard-nav {
    padding: 1rem 2rem;
    border-bottom: 1px solid var(--border);
  }

  .nav-items {
    display: flex;
    gap: 1rem;
    max-width: 1200px;
    margin: 0 auto;
  }

  .nav-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.25rem;
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .nav-item:hover {
    background: var(--bg-primary);
    color: var(--text-primary);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .nav-item.active {
    background: linear-gradient(135deg, var(--primary), var(--info));
    color: white;
    border-color: var(--primary);
  }

  /* Mobile Navigation */
  .mobile-nav-overlay {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--bg-overlay);
    z-index: 2000;
    backdrop-filter: blur(4px);
  }

  .mobile-nav {
    position: absolute;
    top: 0;
    right: 0;
    width: 300px;
    height: 100%;
    background: var(--bg-secondary);
    border-left: 1px solid var(--border);
    padding: 1rem;
    animation: slideIn 0.3s ease;
  }

  @keyframes slideIn {
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
  }

  .mobile-nav .nav-items {
    flex-direction: column;
    gap: 0.5rem;
  }

  .mobile-nav .nav-item {
    width: 100%;
    justify-content: flex-start;
  }

  /* Stats Grid */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1.5rem;
    padding: 2rem;
    max-width: 1200px;
    margin: 0 auto;
  }

  .stat-card {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 1.5rem;
    display: flex;
    gap: 1rem;
    transition: all 0.3s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .stat-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  .stat-icon {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .stat-icon.total {
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: white;
  }

  .stat-icon.present {
    background: linear-gradient(135deg, #10b981, #34d399);
    color: white;
  }

  .stat-icon.absent {
    background: linear-gradient(135deg, #ef4444, #f87171);
    color: white;
  }

  .stat-icon.late {
    background: linear-gradient(135deg, #f59e0b, #fbbf24);
    color: white;
  }

  .stat-content h3 {
    font-size: 1.875rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
    color: var(--text-primary);
  }

  .stat-content p {
    color: var(--text-secondary);
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }

  .stat-trend, .stat-progress {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.75rem;
    color: var(--text-secondary);
  }

  .stat-trend.negative {
    color: var(--danger);
  }

  .progress-bar {
    flex: 1;
    height: 4px;
    background: var(--border);
    border-radius: 2px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    border-radius: 2px;
    transition: width 0.3s ease;
  }

  .progress-text {
    margin-left: 0.5rem;
    font-weight: 600;
  }

  /* Main Content Area */
  .dashboard-main {
    padding: 1rem;
    max-width: 1400px;
    margin: 0 auto;
  }

  .content-area {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  /* Charts Section */
  .charts-section {
    display: grid;
    gap: 1.5rem;
  }

  .chart-card {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .chart-card h4 {
    font-size: 1.125rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: var(--text-primary);
  }

  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
  }

  .chart-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    color: var(--text-secondary);
  }

  .legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .chart-visual {
    display: flex;
    align-items: flex-end;
    gap: 1rem;
    height: 200px;
    padding: 1rem 0;
  }

  .chart-bar-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
  }

  .chart-bar {
    width: 30px;
    border-radius: 6px 6px 0 0;
    transition: all 0.3s ease;
    position: relative;
    min-height: 4px;
  }

  .chart-bar:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  .bar-value {
    position: absolute;
    top: -25px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  .bar-label {
    margin-top: 0.5rem;
    font-size: 0.75rem;
    color: var(--text-secondary);
    text-align: center;
  }

  /* Weekly Chart */
  .weekly-chart {
    display: flex;
    gap: 0.75rem;
    height: 180px;
    padding: 1rem 0;
  }

  .week-day {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: 0.25rem;
  }

  .day-label {
    font-size: 0.75rem;
    color: var(--text-secondary);
    margin-bottom: 0.5rem;
  }

  .day-bars {
    display: flex;
    align-items: flex-end;
    gap: 2px;
    height: 100%;
    width: 100%;
  }

  .day-bar {
    flex: 1;
    border-radius: 4px 4px 0 0;
    transition: all 0.3s ease;
  }

  .day-bar.present {
    background: var(--success);
  }

  .day-bar.late {
    background: var(--warning);
    height: 50%;
  }

  .day-bar.absent {
    background: var(--danger);
  }

  .day-date {
    font-size: 0.75rem;
    color: var(--text-secondary);
    margin-top: 0.5rem;
  }

  /* Monthly Report */
  .monthly-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 1rem;
    margin: 1.5rem 0;
  }

  .monthly-stat {
    background: var(--bg-primary);
    padding: 1rem;
    border-radius: 8px;
    text-align: center;
    border: 1px solid var(--border);
  }

  .stat-label {
    font-size: 0.75rem;
    color: var(--text-secondary);
    margin-bottom: 0.5rem;
  }

  .stat-value {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  .daily-stats-table h5 {
    margin: 1.5rem 0 1rem;
    color: var(--text-secondary);
    font-size: 0.875rem;
    font-weight: 500;
  }

  .table-scroll {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid var(--border);
    border-radius: 8px;
  }

  .table-scroll table {
    width: 100%;
    border-collapse: collapse;
  }

  .table-scroll th {
    padding: 0.75rem 1rem;
    background: var(--bg-primary);
    border-bottom: 1px solid var(--border);
    font-size: 0.75rem;
    text-align: left;
    color: var(--text-secondary);
    position: sticky;
    top: 0;
  }

  .table-scroll td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--border);
    font-size: 0.875rem;
  }

  .table-scroll tr:hover {
    background: var(--bg-primary);
  }

  .attendance-percent {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .percent-bar {
    flex: 1;
    height: 6px;
    background: var(--border);
    border-radius: 3px;
    overflow: hidden;
  }

  .percent-fill {
    height: 100%;
    border-radius: 3px;
    background: var(--success);
    transition: width 0.3s ease;
  }

  /* Top Performers */
  .top-performers-card {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
  }

  .card-header h4 {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1.125rem;
    font-weight: 600;
  }

  .performers-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .performer-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem;
    background: var(--bg-primary);
    border-radius: 8px;
    border: 1px solid var(--border);
  }

  .performer-rank {
    flex-shrink: 0;
  }

  .rank-badge {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    font-weight: 600;
  }

  .rank-1 {
    background: linear-gradient(135deg, #f59e0b, #fbbf24);
    color: white;
  }

  .rank-2 {
    background: linear-gradient(135deg, #94a3b8, #cbd5e1);
    color: white;
  }

  .rank-3 {
    background: linear-gradient(135deg, #92400e, #b45309);
    color: white;
  }

  .performer-avatar {
    width: 36px;
    height: 36px;
    background: linear-gradient(135deg, var(--primary), var(--info));
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    color: white;
    font-size: 0.875rem;
    flex-shrink: 0;
  }

  .performer-info {
    flex: 1;
  }

  .performer-name {
    font-weight: 500;
    color: var(--text-primary);
  }

  .performer-dept {
    font-size: 0.75rem;
    color: var(--text-secondary);
  }

  .performer-score {
    width: 120px;
  }

  .score-bar {
    height: 4px;
    background: var(--border);
    border-radius: 2px;
    overflow: hidden;
    margin-bottom: 0.25rem;
  }

  .score-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--success), #34d399);
    border-radius: 2px;
  }

  .score-details {
    display: flex;
    justify-content: space-between;
    font-size: 0.75rem;
  }

  .score-value {
    color: var(--text-primary);
    font-weight: 600;
  }

  .score-hours {
    color: var(--text-secondary);
  }

  /* Live Updates Panel */
  .live-panel {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: 12px;
    overflow: hidden;
    height: fit-content;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .panel-header {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--border);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .panel-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .panel-title h4 {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  .connection-status {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
  }

  .connection-status.connected {
    background: rgba(16, 185, 129, 0.1);
    color: var(--success);
  }

  .connection-status.disconnected {
    background: rgba(239, 68, 68, 0.1);
    color: var(--danger);
  }

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }

  .connection-status.connected .status-dot {
    background: var(--success);
    animation: pulse 1.5s infinite;
  }

  .connection-status.disconnected .status-dot {
    background: var(--danger);
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  .panel-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .panel-action-btn {
    padding: 0.25rem 0.75rem;
    background: var(--bg-primary);
    border: 1px solid var(--border);
    border-radius: 6px;
    color: var(--text-secondary);
    font-size: 0.75rem;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .panel-action-btn:hover {
    background: var(--danger);
    color: white;
    border-color: var(--danger);
  }

  .panel-toggle {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-primary);
    border: 1px solid var(--border);
    border-radius: 6px;
    color: var(--text-secondary);
    cursor: pointer;
  }

  .panel-toggle:hover {
    background: var(--danger);
    color: white;
    border-color: var(--danger);
  }

  .updates-list {
    max-height: 320px;
    overflow-y: auto;
    padding: 0.5rem;
  }

  .update-item {
    padding: 0.75rem;
    border-radius: 8px;
    margin-bottom: 0.5rem;
    background: var(--bg-primary);
    border-left: 3px solid var(--border);
  }

  .update-checkin {
    border-left-color: var(--success);
  }

  .update-checkout {
    border-left-color: var(--primary);
  }

  .update-manual {
    border-left-color: var(--warning);
  }

  .update-error {
    border-left-color: var(--danger);
  }

  .update-item {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
  }

  .update-icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
  }

  .update-content {
    flex: 1;
  }

  .update-message {
    font-size: 0.813rem;
    color: var(--text-primary);
    line-height: 1.4;
  }

  .update-message strong {
    font-weight: 600;
  }

  .update-dept {
    font-size: 0.75rem;
    color: var(--text-secondary);
    margin-left: 0.25rem;
  }

  .update-time {
    font-size: 0.75rem;
    color: var(--text-secondary);
    margin-top: 0.25rem;
  }

  .no-updates {
    text-align: center;
    padding: 2rem;
    color: var(--text-secondary);
  }

  .no-updates svg {
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  .no-updates p {
    font-size: 0.875rem;
    margin-bottom: 0.25rem;
  }

  .no-updates small {
    font-size: 0.75rem;
  }

  .panel-footer {
    padding: 0.75rem 1.25rem;
    border-top: 1px solid var(--border);
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.75rem;
    color: var(--text-secondary);
  }

  .updates-count {
    font-weight: 500;
  }

  .reconnect-text {
    color: var(--danger);
    animation: pulse 1.5s infinite;
  }

  /* Table Section */
  .table-section {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 1.5rem;
    margin-top: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .section-header h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  .date-display {
    font-size: 0.875rem;
    font-weight: normal;
    color: var(--text-secondary);
    margin-left: 0.5rem;
  }

  .section-actions {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex-wrap: wrap;
  }

  .search-box {
    position: relative;
    display: flex;
    align-items: center;
    background: var(--bg-primary);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    min-width: 240px;
  }

  .search-box svg {
    color: var(--text-secondary);
    flex-shrink: 0;
  }

  .search-box input {
    flex: 1;
    background: transparent;
    border: none;
    color: var(--text-primary);
    padding: 0 0.5rem;
    outline: none;
    font-size: 0.875rem;
  }

  .search-box input::placeholder {
    color: var(--text-secondary);
  }

  .clear-search {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .clear-search:hover {
    background: var(--danger);
    color: white;
  }

  .filter-group {
    display: flex;
    gap: 0.5rem;
  }

  .filter-select {
    padding: 0.5rem 0.75rem;
    background: var(--bg-primary);
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--text-primary);
    font-size: 0.875rem;
    outline: none;
    cursor: pointer;
    min-width: 120px;
  }

  .filter-select option {
    background: var(--bg-card);
    color: var(--text-primary);
  }

  .export-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background: linear-gradient(135deg, var(--primary), var(--info));
    border: none;
    border-radius: 8px;
    color: white;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .export-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(var(--primary-rgb), 0.3);
  }

  .export-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .table-container {
    border: 1px solid var(--border);
    border-radius: 8px;
    overflow: hidden;
  }

  .attendance-table {
    width: 100%;
    border-collapse: collapse;
  }

  .attendance-table thead {
    background: var(--bg-primary);
  }

  .attendance-table th {
    padding: 0.75rem 1rem;
    text-align: left;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid var(--border);
    white-space: nowrap;
  }

  .attendance-table tbody tr {
    border-bottom: 1px solid var(--border);
    transition: all 0.2s ease;
  }

  .attendance-table tbody tr:hover {
    background: var(--bg-primary);
  }

  .attendance-table tbody tr:last-child {
    border-bottom: none;
  }

  .attendance-table td {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
  }

  .employee-cell {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .employee-cell .avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    color: white;
    font-size: 0.875rem;
    flex-shrink: 0;
  }

  .employee-name {
    font-weight: 500;
    color: var(--text-primary);
  }

  .employee-id {
    font-size: 0.75rem;
    color: var(--text-secondary);
  }

  .dept-tag {
    padding: 0.25rem 0.5rem;
    background: var(--bg-primary);
    border: 1px solid var(--border);
    border-radius: 6px;
    font-size: 0.75rem;
    color: var(--text-secondary);
    white-space: nowrap;
  }

  .time-cell {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .late-indicator {
    color: var(--warning);
    font-size: 0.875rem;
  }

  .hours-cell, .overtime-cell {
    font-weight: 500;
    color: var(--text-primary);
  }

  .overtime-tag {
    padding: 0.125rem 0.375rem;
    background: rgba(245, 158, 11, 0.1);
    border: 1px solid var(--warning);
    border-radius: 4px;
    font-size: 0.75rem;
    color: var(--warning);
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.5rem;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
  }

  .action-buttons {
    display: flex;
    gap: 0.25rem;
  }

  .action-btn {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--border);
    border-radius: 6px;
    background: var(--bg-primary);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .action-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .action-btn.view:hover {
    background: var(--primary);
    color: white;
    border-color: var(--primary);
  }

  .action-btn.present:hover:not(:disabled) {
    background: var(--success);
    color: white;
    border-color: var(--success);
  }

  .action-btn.absent:hover:not(:disabled) {
    background: var(--danger);
    color: white;
    border-color: var(--danger);
  }

  .action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .table-footer {
    padding: 1rem;
    border-top: 1px solid var(--border);
    background: var(--bg-primary);
    font-size: 0.875rem;
    color: var(--text-secondary);
    text-align: center;
  }

  .empty-state {
    text-align: center;
    padding: 3rem;
    color: var(--text-secondary);
  }

  .empty-state svg {
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  .empty-state h4 {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 0.5rem;
  }

  .empty-state p {
    margin-bottom: 1rem;
  }

  .clear-filters-btn {
    padding: 0.5rem 1rem;
    background: var(--bg-primary);
    border: 1px solid var(--border);
    border-radius: 6px;
    color: var(--text-primary);
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .clear-filters-btn:hover {
    background: var(--primary);
    color: white;
    border-color: var(--primary);
  }

  /* Loading Screen */
  .loading-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    gap: 1rem;
  }

  .spinner {
    width: 48px;
    height: 48px;
    border: 3px solid var(--border);
    border-top-color: var(--primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .loading-screen p {
    color: var(--text-secondary);
    font-size: 0.875rem;
  }

  /* Error Alert */
  .error-alert {
    margin: 1rem 0;
    padding: 1rem;
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid var(--danger);
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--danger);
    font-size: 0.875rem;
  }

  /* Footer */
  .dashboard-footer {
    margin-top: 2rem;
    padding: 1.5rem 2rem;
    border-top: 1px solid var(--border);
    background: var(--bg-secondary);
  }

  .footer-content {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .footer-content p {
    font-size: 0.875rem;
    color: var(--text-secondary);
  }

  .footer-links {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    font-size: 0.875rem;
    color: var(--text-secondary);
  }

  .connection-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .connection-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .connection-dot.connected {
    background: var(--success);
    animation: pulse 2s infinite;
  }

  .connection-dot.disconnected {
    background: var(--danger);
  }

  .last-updated {
    color: var(--text-secondary);
  }

  /* Modal Styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    backdrop-filter: blur(4px);
  }

  .modal-content {
    background: var(--bg-card);
    border-radius: 12px;
    width: 90%;
    max-width: 500px;
    max-height: 80vh;
    overflow: hidden;
    border: 1px solid var(--border);
    animation: modalSlideIn 0.3s ease;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .modal-header {
    padding: 1.5rem;
    border-bottom: 1px solid var(--border);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .modal-header h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  .modal-close {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-primary);
    border: 1px solid var(--border);
    border-radius: 6px;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .modal-close:hover {
    background: var(--danger);
    color: white;
    border-color: var(--danger);
  }

  .modal-body {
    padding: 1.5rem;
    overflow-y: auto;
  }

  .employee-details {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--border);
  }

  .detail-row:last-child {
    border-bottom: none;
  }

  .detail-label {
    font-weight: 500;
    color: var(--text-primary);
    font-size: 0.875rem;
  }

  .detail-value {
    color: var(--text-secondary);
    text-align: right;
    max-width: 60%;
    word-break: break-word;
    font-size: 0.875rem;
  }

  .modal-footer {
    padding: 1.5rem;
    border-top: 1px solid var(--border);
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
  }

  .modal-btn {
    padding: 0.5rem 1.5rem;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    font-weight: 500;
    font-size: 0.875rem;
    transition: all 0.2s ease;
  }

  .modal-btn.primary {
    background: linear-gradient(135deg, var(--primary), var(--info));
    color: white;
  }

  .modal-btn.primary:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(var(--primary-rgb), 0.3);
  }

  .modal-btn.secondary {
    background: var(--bg-primary);
    color: var(--text-primary);
    border: 1px solid var(--border);
  }

  .modal-btn.secondary:hover {
    background: var(--border);
  }

  /* No Data */
  .no-data {
    text-align: center;
    padding: 2rem;
    color: var(--text-secondary);
  }

  .no-data p {
    font-size: 0.875rem;
  }

  /* Responsive Design */
  @media (max-width: 1024px) {
    .content-area {
      grid-template-columns: 1fr;
    }
    
    .live-panel {
      grid-row: 1;
    }
  }

  @media (max-width: 768px) {
    .dashboard-header {
      padding: 1rem;
      flex-wrap: wrap;
      gap: 1rem;
    }

    .header-left, .header-right {
      width: 100%;
      justify-content: space-between;
    }

    .header-center {
      order: 3;
      width: 100%;
      text-align: center;
    }

    .mobile-menu-btn {
      display: block;
    }

    .dashboard-nav {
      display: none;
    }

    .mobile-nav-overlay {
      display: block;
    }

    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
      padding: 1rem;
      gap: 1rem;
    }

    .chart-visual {
      height: 150px;
    }

    .chart-bar {
      width: 24px;
    }

    .section-header {
      flex-direction: column;
      align-items: stretch;
    }

    .section-actions {
      flex-direction: column;
      align-items: stretch;
    }

    .search-box {
      min-width: auto;
    }

    .filter-group {
      flex-direction: column;
    }

    .filter-select {
      width: 100%;
    }

    .export-btn {
      justify-content: center;
    }

    .attendance-table {
      display: block;
      overflow-x: auto;
    }

    .modal-content {
      width: 95%;
      margin: 1rem;
    }

    .detail-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;
    }

    .detail-value {
      max-width: 100%;
      text-align: left;
    }
  }

  @media (max-width: 480px) {
    .stats-grid {
      grid-template-columns: 1fr;
    }

    .monthly-stats {
      grid-template-columns: repeat(2, 1fr);
    }

    .chart-legend {
      flex-direction: column;
      gap: 0.5rem;
    }

    .panel-actions {
      flex-direction: column;
      align-items: flex-end;
    }

    .action-buttons {
      flex-direction: column;
    }

    .footer-content {
      flex-direction: column;
      text-align: center;
      gap: 0.5rem;
    }

    .footer-links {
      flex-direction: column;
      gap: 0.5rem;
    }
  }
`}</style>
    </div>
  );
};

export default AttendancePage;